import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import axios from "axios";
import {
 Box,
 Typography,
 Button,
 TextField,
 CssBaseline
} from "@mui/material";
import { useHistory } from "react-router-dom";
import { useIsMobile } from "../hooks/useIsMobile";  
import MobileGate from "../components/MobileGate";

const MISSION_GRAY = "#666666";
const MISSION_WHITE = "#FFFFFF";

/**
 * Breaks prose into sentences while preserving punctuation.
 */
function splitIntoSentences(text) {
    return (
        text
            .match(/[^.!?]+[.!?]+|[^.!?]+$/g)
            ?.map((sentence) => sentence.trim()) || []
    );
}

/**
 * MissionParagraph
 *
 * Converts naturally-wrapped browser text into actual visual line
 * elements, allowing every rendered line to have its own independent
 * left-to-right reveal.
 *
 * Important:
 * We do NOT manually determine line length based on character count.
 * The browser remains responsible for typography and line wrapping.
 */
function MissionParagraph({
    paragraph,
    paragraphIndex,
    leftPaneRef
}) {
    const paragraphRef = useRef(null);
    const measurementRef = useRef(null);
    const revealRefs = useRef([]);
    const resizeObserverRef = useRef(null);

    const [lines, setLines] = useState(null);

    /*
     * Tokenize by whitespace rather than by sentence.
     *
     * This allows us to discover the browser's actual line breaks
     * after font metrics, viewport width, letter spacing, etc. have
     * all been applied.
     */
    const words = paragraph.trim().split(/\s+/);

    /**
     * Measure the browser's actual line wrapping.
     *
     * Every word is rendered as an inline element.
     * Words sharing the same offsetTop belong to the same visual line.
     */
    const measureLines = useCallback(() => {
        const container = measurementRef.current;

        if (!container) {
            return;
        }

        const wordElements = Array.from(
            container.querySelectorAll("[data-mission-word]")
        );

        if (!wordElements.length) {
            return;
        }

        const groupedLines = [];

        let currentLine = [];
        let currentTop = null;

        wordElements.forEach((element) => {
            const top = element.offsetTop;

            if (
                currentTop === null ||
                Math.abs(top - currentTop) <= 1
            ) {
                currentLine.push(element.dataset.word);
                currentTop = currentTop ?? top;
            } else {
                groupedLines.push(currentLine);

                currentLine = [element.dataset.word];
                currentTop = top;
            }
        });

        if (currentLine.length) {
            groupedLines.push(currentLine);
        }

        setLines(groupedLines);
    }, []);

    /**
     * Initial measurement + remeasurement after font/layout changes.
     */
    useLayoutEffect(() => {
        measureLines();
    }, [measureLines]);

    /**
     * Recalculate line wrapping whenever the paragraph width changes.
     *
     * This is important for:
     * - window resizing
     * - laptop size changes
     * - browser zoom
     * - responsive breakpoints
     * - font loading
     */
    useEffect(() => {
        const paragraphElement = paragraphRef.current;

        if (!paragraphElement || !window.ResizeObserver) {
            return undefined;
        }

        let frame = null;

        const observer = new ResizeObserver(() => {
            if (frame !== null) {
                window.cancelAnimationFrame(frame);
            }

            /*
             * Temporarily return to measurement mode so the browser
             * can determine the new natural line wrapping.
             */
            frame = window.requestAnimationFrame(() => {
                setLines(null);
            });
        });

        observer.observe(paragraphElement);

        resizeObserverRef.current = observer;

        return () => {
            observer.disconnect();

            if (frame !== null) {
                window.cancelAnimationFrame(frame);
            }
        };
    }, []);

    /**
     * Re-measure after lines have been reset to measurement mode.
     */
    useLayoutEffect(() => {
        if (lines === null) {
            measureLines();
        }
    }, [lines, measureLines]);

    /**
     * Make sure fonts have loaded before final line measurement.
     */
    useEffect(() => {
        if (!document.fonts?.ready) {
            return undefined;
        }

        let cancelled = false;

        document.fonts.ready.then(() => {
            if (!cancelled) {
                setLines(null);
            }
        });

        return () => {
            cancelled = true;
        };
    }, []);

    /*
     * Measurement mode.
     *
     * The user never sees this state because it is visually hidden.
     */
    if (lines === null) {
        return (
            <Typography
    ref={(element) => {
        paragraphRef.current = element;
        measurementRef.current = element;
    }}
    component="p"
    sx={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",

        fontSize: {
            xs: "1.05rem",
            sm: "1.15rem",
            md: "1.3rem"
        },

        lineHeight: {
            xs: 1.9,
            md: 2
        },

        fontWeight: 300,

        mb: {
            xs: paragraphIndex === 8 ? 0 : 4,
            md: paragraphIndex === 8 ? 0 : 5
        },

        visibility: "hidden",
        position: "absolute",
        pointerEvents: "none"
    }}
>
                {words.map((word, index) => (
                    <span
                        key={`${paragraphIndex}-measure-${index}`}
                        data-mission-word
                        data-word={word}
                    >
                        {word}
                        {index < words.length - 1 ? " " : ""}
                    </span>
                ))}
            </Typography>
        );
    }

    return (
        <Typography
    ref={paragraphRef}
    component="p"
    sx={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",

        fontSize: {
            xs: "1.05rem",
            sm: "1.15rem",
            md: "1.3rem"
        },

        lineHeight: {
            xs: 1.9,
            md: 2
        },

        fontWeight: 300,

        mb: {
            xs: paragraphIndex === 8 ? 0 : 4,
            md: paragraphIndex === 8 ? 0 : 5
        },

        overflow: "visible",

        "& .mission-line": {
            display: "block",
            whiteSpace: "nowrap",
            width: "fit-content",
            maxWidth: "100%",
            boxSizing: "border-box"
        }
    }}
>
            {lines.map((line, lineIndex) => (
                <span
                    key={`${paragraphIndex}-line-${lineIndex}`}
                    ref={(element) => {
                        revealRefs.current[lineIndex] = element;
                    }}
                    className="mission-line mission-reading-reveal"
                    data-mission-reveal
                    data-mission-line
                    style={{
                        "--mission-reveal": "0%"
                    }}
                >
                    {line.join(" ")}
                </span>
            ))}
        </Typography>
    );
}
function LandingPage({ onLoginUserChange }) {
 const history = useHistory();
 const isMobile = useIsMobile();

 const [username, setUsername] = useState("");
 const [password, setPassword] = useState("");
 const [loginPrompt, setLoginPrompt] = useState("");

 // =========================================================
// LEFT-PANE SCROLL INDICATOR
// =========================================================

const leftPaneRef = useRef(null);
const firstScrollTargetRef = useRef(null);

const [showScrollCue, setShowScrollCue] = useState(true);

// =========================================================
// MISSION READING REVEAL
// =========================================================

const missionSectionRef = useRef(null);
const missionRafRef = useRef(null);

const updateMissionReveal = useCallback(() => {
    const container = leftPaneRef.current;
    const missionSection = missionSectionRef.current;

    if (!container || !missionSection) {
        return;
    }

    const containerRect = container.getBoundingClientRect();

    /*
     * The "reading line" is approximately 25% into the lower
     * half of the viewport:
     *
     * 50% + (50% × 25%) = 62.5%
     *
     * A line becomes completely white when its top reaches this
     * point.
     */
    const readingLine =
        containerRect.top +
        containerRect.height * 0.625;

    /*
     * Begin the left-to-right transition slightly before the
     * reading line.
     *
     * 90px means the line starts becoming white about 90px before
     * reaching the reading position and is completely white when
     * it reaches the reading position.
     *
     * This is deliberately independent of scroll velocity.
     */
    const revealDistance = Math.max(
        Math.min(containerRect.height * 0.12, 120),
        70
    );

    const revealElements =
        missionSection.querySelectorAll(
            "[data-mission-line]"
        );

    /*
     * Determine whether the user is genuinely at the bottom.
     *
     * This solves the problem where the final line might never
     * geometrically reach the normal reading line because there
     * isn't enough scrollable content beneath it.
     */
    const isAtBottom =
        container.scrollTop +
            container.clientHeight >=
        container.scrollHeight - 2;

    revealElements.forEach((element) => {
        const rect = element.getBoundingClientRect();

        /*
         * Distance from the beginning of the reveal zone.
         *
         * revealStart = readingLine - revealDistance
         */
        const revealStart =
            readingLine - revealDistance;

        const rawProgress =
            (readingLine - rect.top) /
            revealDistance;

        let progress = Math.min(
            Math.max(rawProgress, 0),
            1
        );

        /*
         * Critical end-of-scroll guarantee:
         *
         * When the user reaches the absolute bottom of the left
         * pane, everything in the mission section must be white.
         */
        if (isAtBottom) {
            progress = 1;
        }

        /*
         * Avoid unnecessary style writes.
         */
        const nextValue = `${progress * 100}%`;

        if (
            element.style.getPropertyValue(
                "--mission-reveal"
            ) !== nextValue
        ) {
            element.style.setProperty(
                "--mission-reveal",
                nextValue
            );
        }
    });
}, []);

useEffect(() => {
    const container = leftPaneRef.current;

    if (!container) {
        return undefined;
    }

    let disposed = false;

    const scheduleMissionReveal = () => {
        if (
            disposed ||
            missionRafRef.current !== null
        ) {
            return;
        }

        missionRafRef.current =
            window.requestAnimationFrame(() => {
                missionRafRef.current = null;

                if (!disposed) {
                    updateMissionReveal();
                }
            });
    };

    /*
     * Initial calculation.
     */
    scheduleMissionReveal();

    /*
     * The LEFT PANE is the actual scrolling element.
     */
    container.addEventListener(
        "scroll",
        scheduleMissionReveal,
        { passive: true }
    );

    /*
     * Handle viewport changes.
     */
    window.addEventListener(
        "resize",
        scheduleMissionReveal,
        { passive: true }
    );

    /*
     * Handle changes to the dimensions of the scroll container
     * and mission section.
     */
    const resizeObserver =
        window.ResizeObserver
            ? new ResizeObserver(scheduleMissionReveal)
            : null;

    resizeObserver?.observe(container);

    if (missionSectionRef.current) {
        resizeObserver?.observe(
            missionSectionRef.current
        );
    }

    /*
     * Fonts can change line dimensions after first paint.
     */
    if (document.fonts?.ready) {
        document.fonts.ready.then(
            scheduleMissionReveal
        );
    }

    /*
     * Recalculate after initial layout/paint.
     */
    const initialFrame =
        window.requestAnimationFrame(
            scheduleMissionReveal
        );

    return () => {
        disposed = true;

        container.removeEventListener(
            "scroll",
            scheduleMissionReveal
        );

        window.removeEventListener(
            "resize",
            scheduleMissionReveal
        );

        resizeObserver?.disconnect();

        window.cancelAnimationFrame(
            initialFrame
        );

        if (missionRafRef.current !== null) {
            window.cancelAnimationFrame(
                missionRafRef.current
            );

            missionRafRef.current = null;
        }
    };
}, [updateMissionReveal]);

const updateScrollCue = useCallback(() => {
  const container = leftPaneRef.current;

  if (!container) return;

  const { scrollTop, scrollHeight, clientHeight } = container;

  // User has reached the bottom of the left pane.
  const isAtBottom =
    scrollTop + clientHeight >= scrollHeight - 8;

  // Once the user has demonstrated that they know how to scroll,
  // remove the cue so it doesn't remain distracting.
  const hasScrolled = scrollTop > 40;

  setShowScrollCue(!hasScrolled && !isAtBottom);
}, []);

useEffect(() => {
  const container = leftPaneRef.current;

  if (!container) return;

  updateScrollCue();

  container.addEventListener("scroll", updateScrollCue, {
    passive: true
  });

  return () => {
    container.removeEventListener("scroll", updateScrollCue);
  };
}, [updateScrollCue]);

const scrollDown = useCallback(() => {
  const container = leftPaneRef.current;

  if (!container) return;

  const currentScrollTop = container.scrollTop;

  // Scroll approximately one viewport, rather than jumping
  // directly to the next section.
  const targetScrollTop =
    currentScrollTop + container.clientHeight * 0.85;

  container.scrollTo({
    top: targetScrollTop,
    behavior: "smooth"
  });
}, []);

 if (isMobile) return <MobileGate />;

 const login_submission = async (event) => {
   event.preventDefault();

   try {
     const response = await axios.post(
       "/admin/login",
       {
         signin_name: username,
         password
       },
       {
         withCredentials: true
       }
     );

     setLoginPrompt(response.data.message);

     if (onLoginUserChange) {
       onLoginUserChange(response.data);
     }

     // Redirect after successful login
     history.push("/home");

   } catch (error) {
     setLoginPrompt(
       error?.response?.data?.message || "Login failed"
     );

     if (onLoginUserChange) {
       onLoginUserChange(null);
     }
   }
 };

 return (
    <>
      <CssBaseline />
   <Box
     sx={{
       height: "100vh",
       display: "flex",
       flexDirection: "column",
       bgcolor: "#000"
     }}
   >

     {/* Main Section */}
     <Box
       sx={{
         flex: 1,
         display: "flex",
         overflow: "hidden"
       }}
     >

       {/* LEFT PANE */}
       <Box
        ref={leftPaneRef}
        sx={{
          width: "65%",
          overflowY: "auto",
          position: "relative",
          scrollBehavior: "smooth",

          // Prevent the scrollbar itself from becoming visually dominant.
          scrollbarWidth: "thin",

          "&::-webkit-scrollbar": {
            width: "6px"
          },

          "&::-webkit-scrollbar-track": {
            background: "#000"
          },

          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#444",
            borderRadius: "999px"
          },

          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#666"
          }
        }}
      >

         {/* Hero */}
         <Box
  sx={{
    height: "100vh",
    bgcolor: "#000",
    color: "#FFFDF8",
    px: 6,
    py: 4,
    position: "relative",
    overflow: "hidden"
  }}
>
    {/* Top left logo */}
    <Box
        component="img"
        src="/Colloquia_Logo.png"
        alt="Company Logo"
        sx={{
            position: "absolute",
            top: 40,
            left: 40,

            height: 110,
            width: "auto",

            borderRadius: "20px",
            objectFit: "cover",

            zIndex: 2
        }}
    />

    {/* Center branding */}
    <Box
        sx={{
            height: "100%",

            display: "grid",

            /*
             * Three rows:
             *
             * 1. Reserved space for logo
             * 2. Branding
             * 3. Flexible bottom
             *
             * The branding remains visually centered while never entering
             * the logo's space.
             */
            gridTemplateRows: "150px 1fr 150px",
            // was: gridTemplateRows: "150px 1fr 150px"
            // 1fr alone won't shrink below its content's min size, which is what
            // let the middle row overflow Hero's box on shorter screens.
            gridTemplateRows: "150px minmax(0, 1fr) 150px",

            alignItems: "center"
        }}
    >
        <Box />

        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                gap: 5,
                minHeight: 0, // let this flex column actually shrink instead of overflowing
                "@media (max-height: 900px)": {
                    gap: 3
                }
            }}
        >
            <Typography
                sx={{
                    fontSize: "2.2rem",
                    fontWeight: 300,
                    letterSpacing: "0.25em",
                    "@media (max-height: 900px)": {
                        fontSize: "1.7rem"
                    }
                }}
            >
                Social media where smart people have more social and political influence
            </Typography>

            <Box
                component="img"
                src="/Colloquia_Logo3.png"
                alt="Secondary Logo"
                sx={{
                    width: 400,
                    maxWidth: "100%",
                    height: "auto",
                    objectFit: "contain",
                    borderRadius: "20px",
                    "@media (max-height: 900px)": {
                        width: 300
                    }
                }}
            />
        </Box>

        <Box />
    </Box>

         </Box>
            {/* =========================================================
            SCROLL CUE

            Anchored to the bottom-center of the left pane.
            It remains visible until the user begins scrolling.
            ========================================================= */}
        {showScrollCue && (
          <Box
            component="button"
            type="button"
            aria-label="Scroll down to discover more about Colloquia"
            onClick={scrollDown}
            sx={{
              position: "sticky",
              bottom: 48,
              left: "50%",
              transform: "translateX(-50%)",

              // Make the button occupy no meaningful layout space.
              width: 0,
              height: 0,
              padding: 0,
              margin: 0,
              border: 0,
              background: "transparent",

              display: "flex",
              justifyContent: "center",
              alignItems: "center",

              cursor: "pointer",
              zIndex: 20,

              "&:focus-visible": {
                outline: "none"
              },

              // Respect users who have requested reduced motion.
              "@media (prefers-reduced-motion: reduce)": {
                "& .colloquia-scroll-caret": {
                  animation: "none"
                }
              }
            }}
          >
            <Box
              className="colloquia-scroll-caret"
              aria-hidden="true"
              sx={{
                position: "absolute",

                // Gold associated with the Colloquia visual language.
                width: 22,
                height: 22,

                // Creates a clean, geometric downward caret.
                borderRight: "3px solid #FFFDF8",
                borderBottom: "3px solid #FFFDF8",
                transform: "rotate(45deg)",
                filter:
                  "drop-shadow(0 0 5px rgba(255, 253, 248, 0.35))",
                animation:
                  "colloquia-scroll-bounce 1.8s ease-in-out infinite",
                transition:
                  "filter 180ms ease, opacity 180ms ease",

                "button:hover &": {
                  borderColor: "#FFFFFF",
                  filter:
                    "drop-shadow(0 0 9px rgba(255, 253, 248, 0.65))"
                },

                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none"
                },

                "button:hover &": {
                  borderColor: "#E1C45A",
                  filter:
                    "drop-shadow(0 0 8px rgba(225, 196, 90, 0.65))"
                }
              }}
            />

            {/* Screen-reader-friendly instruction */}
            <Box
              component="span"
              sx={{
                position: "absolute",
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: "hidden",
                clip: "rect(0, 0, 0, 0)",
                whiteSpace: "nowrap",
                border: 0
              }}
            >
              Scroll down
            </Box>
          </Box>
        )}
{/* =========================================================
    IDEAS / ALGORITHM NARRATIVE SECTION

    Each narrative panel is approximately one viewport tall,
    allowing the text and image to be seen together.
    ========================================================= */}
<Box
  ref={firstScrollTargetRef}
  component="section"
  aria-label="The Colloquia philosophy"
  sx={{
    bgcolor: "#000",
    color: "#FFFDF8"
  }}
>
  {/* =========================================================
      PANEL 1

      Text + first image visible together without scrolling.
      ========================================================= */}
  <Box
  sx={{
    height: "100vh",
    minHeight: 0,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    px: {
      xs: 4,
      md: 6,
      lg: 7
    },
    py: {
      xs: 4,
      md: 5
    }
  }}
>
  {/* TEXT */}
  <Box
    sx={{
      width: "100%",
      maxWidth: 760,
      textAlign: "center",
      mb: {
        xs: 3,
        md: 4
      }
    }}
  >
    <Typography
      component="h2"
      sx={{
        fontSize: {
          xs: "1.55rem",
          md: "1.95rem",
          lg: "2.2rem"
        },
        lineHeight: 1.18,
        fontWeight: 300,
        letterSpacing: "-0.02em",
        mb: {
          xs: 2,
          md: 2.5
        }
      }}
    >
        Social media was built to make content popular.
        <br />
        Colloquia is built to make the right people—and the best ideas—popular.
      </Typography>

      <Typography
      sx={{
        maxWidth: 700,
        mx: "auto",
        fontSize: {
          xs: "0.85rem",
          md: "0.95rem",
          lg: "1rem"
        },
        lineHeight: 1.5,
        fontWeight: 300,
        color: "#B8B8B8"
      }}
    >
        Colloquia brings the best forms of social media into one platform powered by novel popularity and virality algorithms designed to give smart people greater visibility and more exposure to high-signal posts—especially, truthful ones—made by other smart people.
      </Typography>
    </Box>

    {/* FIRST IMAGE */}
    <Box
    component="img"
    src="/The_Mothers_of_Colloquia_1.png"
    alt="The Colloquia philosophy"
    sx={{
      display: "block",
      width: "100%",
      maxWidth: 680,
      height: "auto",
      borderRadius: "20px"
    }}
  />
</Box>

  {/* =========================================================
      PANEL 2

      Scroll down to see the next text + image combination.
      ========================================================= */}
  <Box
  sx={{
    height: "100vh",
    minHeight: 0,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    px: {
      xs: 4,
      md: 6,
      lg: 7
    },
    py: {
      xs: 4,
      md: 5
    }
  }}
>
  <Box
    sx={{
      width: "100%",
      maxWidth: 760,
      textAlign: "center",
      mb: {
        xs: 3,
        md: 4
      }
    }}
  >
    <Typography
      sx={{
        fontSize: {
          xs: "1rem",
          md: "1.15rem",
          lg: "1.25rem"
        },
        lineHeight: 1.65,
        fontWeight: 300,
        color: "#E5E5E5"
      }}
    >
        Unlike other platforms optimized for rage-bait, clickbait, brainrot, doomscrolling, “hotness”, bad influencers, and one-sided political ideologies—thereby amplifying low-quality information and misinformation—Colloquia is designed to make smart people—and their ideas—more viral, popular, and influential.
      </Typography>
    </Box>

    {/* SECOND IMAGE */}
    <Box
    component="img"
    src="/The_Mothers_of_Colloquia_2.png"
    alt="Ideas and influence on Colloquia"
    sx={{
      display: "block",
      width: "100%",
      maxWidth: 680,
      height: "auto",
      borderRadius: "20px"
    }}
  />
</Box>
</Box>
         {/* Mission */}
{/* =========================================================
    MISSION / READING REVEAL

    The entire section is black.

    Mission text begins gray.

    As the user scrolls:
        gray
          ↓
        white

    Each sentence reveals from LEFT → RIGHT.

    Because the reveal is controlled by the vertical position
    of each sentence, the aggregate reveal forms a diagonal
    reading front from UPPER-LEFT → LOWER-RIGHT.
    ========================================================= */}

<Box
    ref={missionSectionRef}
    component="section"
    aria-labelledby="mission-title"
    sx={{
        bgcolor: "#000000",
        color: MISSION_WHITE,

        px: {
            xs: 4,
            sm: 6,
            md: 8,
            lg: 10
        },

        /*
         * Large vertical padding gives the reading reveal
         * enough physical space to breathe.
         */
        pt: {
            xs: 12,
            md: 16,
            lg: 20
        },

        pb: {
            xs: 14,
            md: 18,
            lg: 22
        },

        display: "flex",
        flexDirection: "column",
        alignItems: "center",

        textAlign: "center"
    }}
>
    {/* =====================================================
        QUOTE
        ===================================================== */}

    <Typography
        sx={{
            fontSize: {
                xs: "1rem",
                md: "1.1rem"
            },

            lineHeight: 1.7,
            fontStyle: "italic",
            fontWeight: 300,

            color: MISSION_GRAY,

            maxWidth: 800,

            mb: 1
        }}
    >
        The fovndation of every state is the edvcation of its yovth
    </Typography>

    {/* =====================================================
        AUTHOR
        ===================================================== */}

    <Typography
        sx={{
            fontSize: {
                xs: "0.9rem",
                md: "1rem"
            },

            lineHeight: 1.6,
            fontStyle: "italic",
            fontWeight: 300,

            color: "#555555",

            mb: {
                xs: 8,
                md: 10
            }
        }}
    >
        — Diogenes
    </Typography>

    {/* =====================================================
        TITLE
        ===================================================== */}

    <Typography
        id="mission-title"
        component="h2"
        className="mission-reading-reveal"
        data-mission-reveal
        sx={{
            /*
             * The JavaScript will replace this value as the
             * user scrolls.
             */
            "--mission-reveal": "0%",

            fontSize: {
                xs: "2rem",
                sm: "2.3rem",
                md: "2.5rem",
                lg: "2.7rem"
            },

            lineHeight: 1.2,
            fontWeight: 300,

            letterSpacing: "-0.02em",

            maxWidth: 900,

            mb: {
                xs: 7,
                md: 10
            }
        }}
    >
        Addressed to the American Intelligentsia
    </Typography>

    {/* =====================================================
        MISSION BODY
        ===================================================== */}

    <Box
        sx={{
            width: "100%",
            maxWidth: 900,

            position: "relative",
            display: "flex",
            flexDirection: "column",

            alignItems: "flex-start",
            textAlign: "left"
        }}
    >
        {[
           "Our goal here at Colloquia is for smart people to have significantly more social and political influence.",
            "Attention fellow Americans, we are on the brink of the greatest paradigm shift in the history of the United States. As an intellectual societal assemblage on the Colloquia social media platform, we, the people, will ameliorate the American body politic and the social fabric of this nation. At Colloquia, we emphasize that it is our operating doctrine that we must empower intellectuals and elevate the prominence of intellectualism in American culture. Together, we will form a more intelligent union.",
            "Right now, society venerates and mainly gives camera time to jocks, jerks, bullies, “pretty mean girls”, high-school-cool cliques, anti-intellectuals, and people valued mainly for their looks but lacking moral and intellectual substance. In general, these “cool people” can more easily amass a tidal wave of views and engagement on social platforms—often through what we call “brainrot” material—and exert whatever influence they want on our youth and society as a whole. If we follow this social dynamic up the American social structure, we see that this pestilence infects business and politics, the twin pillars of American society; if they become too brittle, society collapses. Business should operate such that we preserve the American Dream where any American citizen can pursue their vocation and—if they work smart and hard enough—reach the highest stages in business. However, it is a truism that subjectivity exists within organizational structures which—if overly discriminatory, unfair, or riddled with incompetence—can undermine the operating environment that rewards smarts, competence, and merit and ultimately stifle American innovation. On the politics pillar, America bleeds if there is stupidity and incompetence in her government. The long-standing U.S. electoral system can actually lead to an idiocracy since the following scenario is possible: suppose there are candidates A and B running for office. 5 smart people choose candidate A but 10 stupid people choose candidate B. Candidate B wins and uses the bully pulpit to spread the shared values they had with the 10 stupid people who voted for them to the rest of Americans. On the coverage of voter influence, Cardi B, Nicki Minaj, and Jake Paul all individually have far more political influence than an academically distinguished political science professor. Our question to you all is: do you want politics to be influenced by the supermajority of laypeople, unlearned people, and fools (as is already the case) or by intellectuals? The Colloquia social media platform will destine us to a powerful American sophocracy.",
            "The title “influencer” clearly has the word “influence” in it and we here at Colloquia want smart people to be the ones influencing our youth and society as a whole.",
            "Colloquia is every social media form wrapped into one but the social power is bestowed upon the intelligentsia. On this platform, voting power is apportioned such that it is commensurate with one’s intellectual achievements. That is to say that “power votes” (a feature similar to vanilla “upvotes” in legacy social media apps), will be the dominant force that circulates popular media within this platform. ",
            "We call upon thee, who lovest mathematics, physics, engineering, computer science, chemistry, biology, medicine, English, philosophy, economics, politics, history, law, or any other subjects of intellectual beauty, to join this intellectual community.",
            "Together we will have the equanimity and propriety needed in often fraught political discourse to legislate optimal solutions for the betterment of the republic.",
            "We here at Colloquia have the sensational will to fundamentally improve the current American social order and the body politic and ultimately form a more pro-intellectual American society.",
            "With this voting power vested in you all—the intellectually magnificent—together we will bloom a new era: an American renaissance run by the intelligentsia, where truth prevails, and we will achieve an everlasting paradise in this age of American superintelligence."
        ].map((paragraph, paragraphIndex) => (
    <MissionParagraph
        key={`mission-paragraph-${paragraphIndex}`}
        paragraph={paragraph}
        paragraphIndex={paragraphIndex}
        leftPaneRef={leftPaneRef}
    />
))}

        {/* =================================================
            SIGNATURE
            ================================================= */}

       <Box
            sx={{
                mt: {
                    xs: 8,
                    md: 10
                }
            }}
        >
            <Typography
                className="mission-reading-reveal"
                data-mission-reveal
                sx={{
                    "--mission-reveal": "0%",

                    fontSize: {
                        xs: "1rem",
                        md: "1.1rem"
                    },

                    lineHeight: 1.7,

                    fontStyle: "italic",
                    fontWeight: 300
                }}
            >
                With sincerity,
            </Typography>

            <Typography
                className="mission-reading-reveal"
                data-mission-reveal
                sx={{
                    "--mission-reveal": "0%",

                    fontSize: {
                        xs: "1rem",
                        md: "1.1rem"
                    },

                    lineHeight: 1.7,

                    fontStyle: "italic",
                    fontWeight: 300
                }}
            >
                Colloquia Executive Department
            </Typography>
        </Box>
    </Box>
</Box>
</Box>
       {/* RIGHT LOGIN PANE */}
       <Box
         sx={{
           width: "35%",
           bgcolor: "#1A1A1A",
           display: "flex",
           justifyContent: "center",
           alignItems: "center",
           px: 5
         }}
       >

         <Box
           component="form"
           onSubmit={login_submission}
           sx={{
             width: "100%",
             maxWidth: 420
           }}
         >

           <Typography
             sx={{
               color: "#FFFDF8",
               mb: 5,
               fontSize: "2rem",
               fontWeight: 300
             }}
           >
             Member Access
           </Typography>

           <TextField
                fullWidth
                label="Username or .edu/.ac.uk Email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                margin="normal"
                variant="outlined"
                sx={{
                    "& .MuiInputBase-input": {
                    color: "#FFFDF8", // typed text
                    },

                    "& .MuiInputLabel-root": {
                    color: "#B8B8B8", // label color
                    },

                    "& .MuiOutlinedInput-root": {
                    backgroundColor: "#262626", // slightly lighter than pane
                    borderRadius: "12px",

                    "& fieldset": {
                        borderColor: "#555", // default border
                    },

                    "&:hover fieldset": {
                        borderColor: "#999", // hover border
                    },

                    "&.Mui-focused fieldset": {
                        borderColor: "#FFFDF8", // focus border
                    }
                    }
                }}
            />

            <TextField
                fullWidth
                type="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                variant="outlined"
                sx={{
                    "& .MuiInputBase-input": {
                    color: "#FFFDF8",
                    },

                    "& .MuiInputLabel-root": {
                    color: "#B8B8B8",
                    },

                    "& .MuiOutlinedInput-root": {
                    backgroundColor: "#262626",
                    borderRadius: "12px",

                    "& fieldset": {
                        borderColor: "#555",
                    },

                    "&:hover fieldset": {
                        borderColor: "#999",
                    },

                    "&.Mui-focused fieldset": {
                        borderColor: "#FFFDF8",
                    }
                    }
                }}
            />

           <Button
             fullWidth
             type="submit"
             variant="contained"
             sx={{
               mt: 4,
               py: 1.8,
               bgcolor: "#000"
             }}
           >
             Log In
           </Button>

           <Button
             fullWidth
             variant="outlined"
             onClick={() =>
               history.push("/register")
             }
             sx={{
               mt: 2,
               py: 1.8,
               color: "#FFFDF8",
               borderColor: "#555"
             }}
           >
             Create New Account
           </Button>

           {loginPrompt && (
             <Typography
               sx={{
                 mt: 3,
                 color: "#AAA"
               }}
             >
               {loginPrompt}
             </Typography>
           )}

         </Box>

       </Box>

     </Box>

     {/* FOOTER */}
     {/*<Box
       sx={{
         height: "100px",
         bgcolor: "#000",
         borderTop: "1px solid #222",
         display: "flex",
         alignItems: "center",
         justifyContent: "space-between",
         px: 5
       }}
     >

       <Button
         sx={{
           color: "#FFFDF8"
         }}
         onClick={() => history.push("/contact")}
       >
         Contact
       </Button>

       <Typography
         sx={{
           color: "#777",
           fontSize: ".9rem"
         }}
       >
         © 2026 Colloquia from Penneferies Squire Capital
       </Typography>

     </Box>*/}
     {/* FOOTER */}
<Box
  component="footer"
  sx={{
    minHeight: "100px",
    bgcolor: "#000",
    borderTop: "1px solid #222",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    px: {
      xs: 3,
      md: 5
    },
    py: 2,
    gap: 2,
    flexWrap: "wrap"
  }}
>
  {/* Legal / Company Navigation */}
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: {
        xs: 0.5,
        sm: 1
      }
    }}
  >
    {[
      { label: "About", path: "/about" },
      { label: "Terms", path: "/terms" },
      { label: "Privacy", path: "/privacy" },
      { label: "Careers", path: "/careers" },
      { label: "Support", path: "/support" },
      { label: "Contact", path: "/contact" }
    ].map(({ label, path }) => (
      <Button
        key={label}
        onClick={() => history.push(path)}
        sx={{
          color: "#888",
          minWidth: "auto",
          px: {
            xs: 1,
            sm: 1.25
          },
          py: 0.75,
          fontSize: {
            xs: "0.72rem",
            sm: "0.8rem"
          },
          fontWeight: 300,
          letterSpacing: "0.04em",
          textTransform: "none",
          borderRadius: "6px",

          "&:hover": {
            color: "#FFFDF8",
            backgroundColor: "transparent"
          },

          "&:focus-visible": {
            outline: "1px solid #666",
            outlineOffset: "2px"
          }
        }}
      >
        {label}
      </Button>
    ))}
  </Box>

  {/* Copyright */}
  <Typography
    sx={{
      color: "#666",
      fontSize: {
        xs: "0.7rem",
        sm: "0.8rem"
      },
      fontWeight: 300,
      whiteSpace: {
        xs: "normal",
        sm: "nowrap"
      },
      textAlign: {
        xs: "center",
        sm: "right"
      }
    }}
  >
    © 2026 Colloquia from Penneferies Squire Capital
  </Typography>
</Box>

   </Box>
           <style>
{`
    /*
     * ============================================================
     * COLLOQUIA MISSION LINE REVEAL
     * ============================================================
     *
     * Each rendered line has its own --mission-reveal value.
     *
     * 0%:
     *     Entire line = gray
     *
     * 50%:
     *     Left half = white
     *     Right half = gray
     *
     * 100%:
     *     Entire line = white
     */

    .mission-reading-reveal {
        --mission-reveal: 0%;

        background-image: linear-gradient(
            90deg,
            ${MISSION_WHITE} 0%,
            ${MISSION_WHITE} var(--mission-reveal),
            ${MISSION_GRAY} var(--mission-reveal),
            ${MISSION_GRAY} 100%
        );

        background-repeat: no-repeat;
        background-size: 100% 100%;

        background-clip: text;
        -webkit-background-clip: text;

        color: transparent;
        -webkit-text-fill-color: transparent;

        /*
         * Required for Safari/WebKit when text clipping is used.
         */
        -webkit-box-decoration-break: clone;
        box-decoration-break: clone;
    }

    /*
     * Keep the line reveal extremely smooth without introducing
     * a CSS transition that could cause lag behind fast scrolling.
     *
     * JavaScript updates --mission-reveal once per animation frame.
     */

    @keyframes colloquia-scroll-bounce {
        0%,
        100% {
            transform: rotate(45deg) translate(0, 0);
            opacity: 0.55;
            filter: drop-shadow(
                0 0 3px rgba(255, 253, 248, 0.20)
            );
        }

        50% {
            transform: rotate(45deg) translate(0, 8px);
            opacity: 1;
            filter: drop-shadow(
                0 0 8px rgba(255, 253, 248, 0.55)
            );
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .colloquia-scroll-caret {
            animation: none !important;
        }
    }
`}
</style>
   </>
 );
}

export default LandingPage;